import React, { Children, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Tabs from '@mui/material/Tabs'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'

const AdxTabsScrollable = ({ whiteBackground, sxTabs, sxPanel, children, panelGap = 0 }) => {
  const [value, setValue] = useState(0)
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }
  const arrayChildren = Children.toArray(children)

  // create multip ref based length of children arrey
  const refPanels = useMemo(
    () =>
      Array(arrayChildren.length)
        .fill(0)
        .map((i) => React.createRef()),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const refParent = useRef('refParent')

  const panelScrollHeight = (index) => {
    if (index === 0) {
      return 0
    } else {
      let totalHeight = 0
      for (let i = 0; i < index; i++) {
        totalHeight += refPanels[i].current.offsetHeight + panelGap
      }
      return totalHeight
    }
  }

  const scrollToDiv = (index) => {
    if (refPanels[index]) {
      refParent.current.scrollTo({
        behavior: 'smooth',
        top: panelScrollHeight(index),
      })
    }
  }

  const handleClick = useCallback((index) => {
    scrollToDiv(index)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = refParent.current
      const reachedBottom = Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1

      for (let i = 0; i < arrayChildren.length; i++) {
        const panelTop = panelScrollHeight(i)
        const panelBottom = panelScrollHeight(i + 1)
        if (scrollTop >= panelTop && scrollTop < panelBottom) {
          setValue((prev) => (prev !== i ? i : prev))
        }
        if (reachedBottom) {
          setValue((prev) => (prev !== arrayChildren.length - 1 ? arrayChildren.length - 1 : prev))
        }
      }
    }

    if (refParent.current) {
      refParent.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (refParent.current) {
        refParent.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [refParent.current.scrollTop])

  return (
    <Box
      height={1}
      bgcolor={whiteBackground ? 'white.main' : null}
      borderRadius={whiteBackground ? '4.5px' : null}
      sx={{ width: '100%', ...sxTabs }}
      display={'flex'}
      flexDirection={'column'}
      paddingY={whiteBackground ? 1 : null}
    >
      <Box
        boxSizing="border-box"
        display={'flex'}
        flexDirection={'row'}
        borderBottom={1}
        borderColor={'gray-line.main'}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          scrollButtons={false}
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{
            sx: {
              backgroundColor: 'alternate.main',
              height: '2.5px',
            },
          }}
        >
          {arrayChildren.map((item, index) => (
            <Tab
              key={index}
              value={index}
              onClick={() => handleClick(index)}
              sx={{
                fontSize: '16px',
                fontSizeLg: '16px',
              }}
              label={
                <Box display={'flex'} flexDirection="row" alignContent="center">
                  <Typography
                    variant="body1"
                    color={value === index ? 'alternate.main' : 'light-gray-text.main'}
                    fontWeight={'medium'}
                  >
                    {item.props.label ? item.props.label : `Tab ${[index]}`}
                  </Typography>

                  {item.props.notification ? (
                    <Box
                      marginLeft="10px"
                      width="45px"
                      borderRadius="4.5px"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center"
                      bgcolor="#E1F3F0"
                    >
                      <Typography variant="body2" fontWeight={600} color="alternate.main">
                        {item.props.notification}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
      <Box
        ref={refParent}
        bgcolor={!whiteBackground ? 'white.main' : null}
        borderRadius={!whiteBackground ? '4.5px' : null}
        sx={{
          overflowY: 'auto',
          ...sxPanel,
        }}
        className="scrollbar"
      >
        {arrayChildren.map((item, index) => (
          <Box key={index} ref={refPanels[index]}>
            {item}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default AdxTabsScrollable
